/** @format */

import React, {useContext, useState} from 'react';
import SuperAgent from 'superagent';

import {
    Alert,
    Popover,
    Button,
    IconButton,
    Select,
    Icon,
    QuoterValidation,
    Modal,
    ModalHeader,
    ModalFooter
} from '@sttark/design-system';

import {formatMoney, formatNumber, formatShortDate} from './functions/format';
import {
    ORDER_EXPEDITED_PROCESSING,
    ORDER_STANDARD_PROCESSING,
    ORDER_STATUS_IN_CART,
    ORDER_STATUS_QUOTE_SAVED,
    PRODUCT_TYPE_CARTON,
    PROOF_APPROVAL_CUTOFF,
    PROOF_TYPE_ELECTRONIC,
    PROOF_TYPE_NONE,
    PROOF_TYPE_PRINTED,
    ORDER_CUTOFF
} from 'frontier-constants';

import {QuoteContext} from 'frontier-quoter';
import Section from './section';
import {ShippingEstimator} from './shipping_estimator';
import useRequest from './hooks/use_request';
import '../../account/namespaced_bs4.sass';
import QuoteLoginModal from './quote_login_modal';
import ModalPortal, {ModalBody, ModalClose} from 'frontier-admin/modal/container_portal';

import './order_summary.sass';
import {AccountContext, ConditionalButton, If} from 'frontier-utilities';
import {addCommas} from 'frontier/utilities/add_comas_to_number';
import {DateFromNow, getLocalTime, IfBeforeCutoff} from 'frontier/utilities/format/date';

export default function OrderSummary({isCustomFee}) {
    const ctx = useContext(QuoteContext);
    const order = ctx.order;

    const {isComplete, errors} = ctx.handleIsComplete(order);

    const [displayLoginModal, setDisplayLoginModal] = useState(false);
    const [showCreateAccount, setShowCreateAccount] = useState(true);
    const [showModal, setShowModal] = useState(0);

    const toggleDisplayLoginModal = () => {
        setDisplayLoginModal(!displayLoginModal);
    };

    const saveQuoteAndToggleDisplayLoginModal = () => {
        handleSaveQuote(order.id);
        setDisplayLoginModal(!displayLoginModal);
    };

    const handleChangeProof = v => {
        const [proof_type_id, is_flat_printed_proof] = v.split('_');

        // If embossing is enabled for carton orders, prevent selecting "Printed Proof Only"
        // but allow regular "Printed Proof"
        if (
            ctx.order.product_type_id === PRODUCT_TYPE_CARTON &&
            ctx.order.is_embossing &&
            parseInt(proof_type_id) === PROOF_TYPE_PRINTED &&
            parseInt(is_flat_printed_proof) === 1
        ) {
            return;
        }

        ctx.handleChange({
            order: {
                proof_type_id: {$set: parseInt(proof_type_id)},
                is_flat_printed_proof: {$set: parseInt(is_flat_printed_proof)}
            }
        });
    };

    const {
        error: saveQuoteError,
        isPending: isSavingToAccount,
        request: handleSaveQuote
    } = useRequest(
        null,
        async () => {
            const date = formatShortDate(new Date());
            const time = new Date().toLocaleTimeString();
            const label = `Saved Quote - ${formatShortDate(date)} ${time}`;

            const {
                body: {order}
            } = await SuperAgent.post(`/carts/save-to-account/${ctx.order.id}`)
                .set('accept', 'application/json')
                .send({customer_label: label});

            ctx.handleSaveToAccount(order);
        },
        {initial: {}}
    );

    const handleLogin = async response => {
        const {isLoggedIn, userId} = response;

        // save order only after user logs in
        isLoggedIn && handleSaveQuote();

        // If they're logged in, pass it to the context, so we can show the banner and allow further saved quotes.
        ctx.handleLogin(true);
        toggleDisplayLoginModal();
    };

    const handleShowCreateAccount = showCreateAccount => {
        setShowCreateAccount(showCreateAccount);
    };

    let isPrintedProofOnlyPossible =
        ctx.designs_orders.filter(design => design.qty !== 1).length === 0;

    if (ctx.designs_orders.length === 0) {
        // Skipping Artwork Upload
        isPrintedProofOnlyPossible =
            ctx.order.total_qty === ctx.order.design_qty && ctx.order.total_qty > 0;
    }

    // is it new order ora an order edit?
    const isOrderEdit = parseInt(ctx.order.status_id) === ORDER_STATUS_IN_CART ? true : false;

    let proofOptions = [];

    if (ctx.order.product_type_id === PRODUCT_TYPE_CARTON) {
        // Base options for carton orders
        proofOptions = [
            {label: 'No Proof', value: `${PROOF_TYPE_NONE}_0`, rightLabel: {label: 'Free'}},
            {
                label: 'Digital Proof',
                value: `${PROOF_TYPE_ELECTRONIC}_0`,
                rightLabel: {label: 'Free'}
            }
        ];

        // Add printed proof options based on embossing and printed proof only capability
        if (!ctx.order.is_embossing) {
            // If embossing is not selected, show the appropriate printed proof option
            if (isPrintedProofOnlyPossible) {
                proofOptions.push({
                    label: 'Printed Proof Only',
                    value: `${PROOF_TYPE_PRINTED}_1`,
                    rightLabel: {
                        label: '$'
                    },
                    description:
                        'Samples come pre-cut and creased, with proof tape included for easy self-assembly.'
                });
            } else {
                // Otherwise add the regular "Printed Proof" option
                proofOptions.push({
                    label: 'Printed Proof',
                    value: `${PROOF_TYPE_PRINTED}_1`,
                    rightLabel: {
                        label: '$'
                    },
                    description:
                        'Samples come pre-cut and creased, with proof tape included for easy self-assembly.'
                });
            }
        } else {
            // If embossing is selected, only show printed proof if it would NOT be printed proof only
            if (!isPrintedProofOnlyPossible) {
                proofOptions.push({
                    label: 'Printed Proof',
                    value: `${PROOF_TYPE_PRINTED}_0`,
                    rightLabel: {
                        label: '$'
                    },
                    description:
                        'Samples come pre-cut and creased, with proof tape included for easy self-assembly.'
                });
            }
        }
    } else {
        proofOptions = [
            {label: 'No Proof', value: `${PROOF_TYPE_NONE}_0`},
            {
                label: 'Digital Proof',
                value: `${PROOF_TYPE_ELECTRONIC}_0`
            },
            {
                label: isPrintedProofOnlyPossible ? 'Printed Proof Only' : 'Printed Proof',
                value: `${PROOF_TYPE_PRINTED}_0`
            }
        ];
    }

    function handleVerifyAuthAndSaveQuote() {
        // if they are logged in, save the quote
        if (order.status_id !== ORDER_STATUS_QUOTE_SAVED && ctx.isLoggedIn) {
            handleSaveQuote();
            setShowModal(0);
        } else if (
            // otherwise, make them log in first before saving
            order.status_id !== ORDER_STATUS_QUOTE_SAVED &&
            !ctx.isLoggedIn
        ) {
            setShowModal(0);
            toggleDisplayLoginModal();
        } else {
            setShowModal(0);
            saveQuoteAndToggleDisplayLoginModal();
        }
    }

    return (
        <>
            <Modal
                onClose={() => setShowModal(0)}
                isOpen={showModal === 1}
                zIndex={1050}
                bodyHeightAuto>
                <ModalHeader>Save Quote</ModalHeader>
                <div>
                    <p className="mb-2 mt-0 mx-0">
                        <b>You are about to save your current quote</b>
                    </p>
                    <p className="mt-0">
                        Saving this quote will move it to Saved Quotes on your account and you will
                        start from a brand new quote. Do you want to continue?
                    </p>
                </div>
                <ModalFooter>
                    <div className="d-flex align-items-center justify-content-end gap-4">
                        <Button onClick={() => setShowModal(0)} label="Cancel" secondary />
                        <Button onClick={handleVerifyAuthAndSaveQuote} label="Save & Start Over" />
                    </div>
                </ModalFooter>
            </Modal>

            <Select
                initialValue={
                    proofOptions.find(
                        ({value}) =>
                            value === `${order.proof_type_id}_${order.is_flat_printed_proof}`
                    )?.label || ''
                }
                placeholder="Select Proof Type"
                name="proof"
                label={null}
                options={proofOptions}
                value={`${order.proof_type_id}_${order.is_flat_printed_proof}`}
                onChange={handleChangeProof}
            />
            {(order.is_flat_printed_proof === 1 ||
                (order.product_type_id === PRODUCT_TYPE_CARTON &&
                    order.proof_type_id === PROOF_TYPE_PRINTED)) && (
                <div className="hstack justify-content-between font-body-sm">
                    <span style={{display: 'flex', alignItems: 'center'}}>
                        <div className="me-2">
                            <Icon name="warning" color="#e5b200" large />
                        </div>
                        <ConditionalButton enabled={parseInt(order.is_flat_printed_proof) === 1}>
                            <p className="m-0 font-body-sm">
                                Samples come pre-cut and creased, with proof tape included for easy
                                self-assembly.
                            </p>
                            <p className="m-0 font-body-sm">
                                Printed & Folded Proofs are cut on a plotter, hand folded, and glued
                                with proof tape. Only the Print is reflective of the production
                                quality. The production quality is reflective of the fact that it is
                                a proof.
                            </p>
                        </ConditionalButton>
                    </span>
                </div>
            )}
            <Section enabled={!ctx.isAddingToCart && !ctx.isSaving}>
                <div className="my-4">
                    {/* price per label */}
                    {order.total_qty && (
                        <div className="hstack justify-content-between font-body-sm">
                            <span>
                                {formatNumber(
                                    order.duplex_aware_total_qty_with_split ||
                                        order.duplex_aware_total_qty
                                )}{' '}
                                {ctx.pluralProduct}
                            </span>
                            <span>
                                $
                                {Math.ceil(
                                    ((order.quoted_total - order.quoted_die_charge) /
                                        (order.duplex_aware_total_qty_with_split ||
                                            order.duplex_aware_total_qty)) *
                                        1000
                                ) / 1000}{' '}
                                per {ctx.singularProduct}
                            </span>
                        </div>
                    )}

                    {/* custom fee */}
                    {isCustomFee && order.total_qty > 0 && order.quoted_die_charge !== '0.00' && (
                        <div>
                            <div className="hstack justify-content-between font-body-sm">
                                <span style={{display: 'flex', alignItems: 'center'}}>
                                    <div className="summary-warning-icon">
                                        <Icon small name="alert" />
                                    </div>
                                    <p className="m-0 font-body-sm">
                                        <span className="font-body-bold-sm">One-Time</span> Custom
                                        Die Fee
                                    </p>
                                </span>
                                <span>{formatMoney(order.quoted_die_charge)}</span>
                            </div>

                            {order.product_type_id === PRODUCT_TYPE_CARTON && (
                                <div>
                                    <div className="p-4 my-4 content-box bg-structure-lighter">
                                        <h5 className="mt-0 font-label-xs mb-1">
                                            Want to save money?
                                        </h5>
                                        <p className="founders-reg" style={{fontSize: '14px'}}>
                                            Round your designs to the nearest half inch. Our Experts
                                            can even adjust your artwork for you.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="hstack justify-content-between font-body-bold-xl">
                        <span style={{display: 'flex', alignItems: 'center'}}>
                            Subtotal
                            {(ctx.isSaving || ctx.isAddingToCart) && (
                                <span className="saving font-body-sm pt-2 ps-2">
                                    <i className="fa fa-spinner fa-spin"></i>
                                </span>
                            )}
                        </span>
                        <span className="">
                            {addCommas(
                                formatMoney(order.total_qty > 0 ? order.quoted_total : null)
                            )}
                        </span>
                    </div>
                </div>
            </Section>
            <Button
                fullWidth
                className="mb-3"
                label={isOrderEdit ? "I'm Finished, Back to Cart" : 'Add to Cart'}
                onClick={ctx.handleAddToCart}
                disabled={!isComplete || ctx.isAddingToCart || isSavingToAccount || ctx.isSaving}
            />
            {order.status_id !== ORDER_STATUS_QUOTE_SAVED && (
                <Button
                    secondary
                    fullWidth
                    label={isOrderEdit ? 'Changes are Autosaved' : 'Save Quote'}
                    disabled={isOrderEdit}
                    onClick={() => setShowModal(1)}
                />
            )}

            {displayLoginModal && (
                <ModalPortal onClose={toggleDisplayLoginModal} noCard={true} maxWidth="700px">
                    <div style={{background: 'white', padding: '20px', position: 'relative'}}>
                        <ModalClose onClose={toggleDisplayLoginModal} />
                        <QuoteLoginModal
                            orderId={ctx.order.id}
                            defaultLabel={ctx.order.customer_label}
                            onLogin={handleLogin}
                            onShowCreateAccount={handleShowCreateAccount}
                        />
                    </div>
                </ModalPortal>
            )}

            {order.status_id === ORDER_STATUS_QUOTE_SAVED && (
                <p className="text-center">Quote Saved Automatically</p>
            )}

            {saveQuoteError && (
                <div className="mt-2">
                    <Alert title="">
                        There was a problem saving this quote: {saveQuoteError.message}
                    </Alert>
                </div>
            )}

            <OrderValidation order={order} errors={errors} />

            <Section enabled={ctx.canEstimateShipping(ctx.order)} className="mt-8">
                <div className="mt-6">
                    <p>
                        <strong>Need it faster?</strong> Upgrade processing time.{' '}
                        <Popover
                            title="Expedited Processing"
                            content={
                                <p>
                                    Expediting your order means skipping to the front of the line.
                                    Our expedite fee varies daily based on order volume and
                                    capacity. Our expedite fee may be higher during busier times
                                    because you are skipping a much longer line. However, you'll be
                                    paying a much smaller fee when the line is shorter.
                                </p>
                            }>
                            <IconButton secondary icon="info" />
                        </Popover>
                    </p>

                    <Select
                        initialValue={
                            order.processing_id === ORDER_STANDARD_PROCESSING
                                ? 'Standard Processing'
                                : order.processing_id === ORDER_EXPEDITED_PROCESSING
                                  ? 'Expedited Processing'
                                  : ''
                        }
                        label={null}
                        options={[
                            {label: 'Standard Processing', value: ORDER_STANDARD_PROCESSING},
                            {label: 'Expedited Processing', value: ORDER_EXPEDITED_PROCESSING}
                        ]}
                        value={order.processing_id}
                        onChange={v => ctx.handleChange({order: {processing_id: {$set: v}}})}
                    />
                </div>
                <div>
                    <h4 className="mb-2">
                        Shipment Details{' '}
                        <Popover
                            title="Estimated Shipping"
                            content={
                                <p>
                                    Order shipping is an estimation based on the purchase date and
                                    proof approval. Delivery date is also an estimation that may be
                                    impacted by carrier delays and holidays/weekends.
                                </p>
                            }>
                            <IconButton secondary icon="info" />
                        </Popover>
                    </h4>
                    <div className="d-flex align-items-start justify-content-start shipment-details-warning mb-5">
                        <div className="me-2">
                            <Icon name="warning" color="#e5b200" large />
                        </div>
                        <p className="p-0 m-0 font-body-md">
                            <b className="font-body-bold-md">Please note:</b> All shipping and
                            arrival dates are estimates and may vary
                        </p>
                    </div>
                    <If
                        condition={
                            order.processing_id === ORDER_EXPEDITED_PROCESSING &&
                            order.delay_processing === 1
                        }>
                        <IfBeforeCutoff cutoffTime={ORDER_CUTOFF}>
                            <div className="d-flex align-items-start justify-content-start shipment-details-warning mb-5">
                                <div className="me-2">
                                    <Icon name="warning" color="#e5b200" large />
                                </div>
                                <p className="p-0 m-0 font-body-md">
                                    Order by{' '}
                                    <b className="font-body-bold-md">{ORDER_CUTOFF}am ET</b> <br />
                                    with final proof approval by{' '}
                                    <b className="font-body-bold-md">
                                        {PROOF_APPROVAL_CUTOFF}pm ET
                                    </b>
                                    <p className="p-0 m-0 font-body-md">
                                        Time remaining until 10am ET:{' '}
                                        <b className="font-body-bold-md">
                                            <DateFromNow withoutSuffix={true}>
                                                {getLocalTime(ORDER_CUTOFF)}
                                            </DateFromNow>
                                        </b>
                                    </p>
                                </p>
                            </div>
                        </IfBeforeCutoff>
                    </If>
                </div>
                <ShippingEstimator />
            </Section>
        </>
    );
}

function OrderValidation({errors}) {
    const hasErrors = errors && errors.length > 0;

    if (!hasErrors) return null;

    return (
        <div className="mt-4 w-100">
            <QuoterValidation
                icon="warning"
                subTitle="Items Incomplete"
                steps={errors.map(e => e.message)}
            />
        </div>
    );
}
